import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { decodeJWT } from '../../utils/jwt';

interface UserPayload {
  nameid: string; // User ID in ASP.NET Core default JWT claims
  email: string;
  role: string | string[];
  permissions: string[];
  fullName?: string;
  cabinetName?: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  currentUser: UserPayload | null;
  loading: boolean;
  error: string | null;
}

const getInitialState = (): AuthState => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    let decoded = decodeJWT(token) as any;
    if (decoded) {
      // Handle multiple permissions which can be a string or array of strings
      let perms = decoded.permission || [];
      if (typeof perms === 'string') perms = [perms];

      const normalizedUser: UserPayload = {
        nameid: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] || decoded.sub || decoded.nameid,
        email: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] || decoded.email,
        role: decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || decoded.role,
        permissions: perms,
        fullName: decoded.fullName,
        cabinetName: decoded.groupLabel,
      };

      return {
        isAuthenticated: true,
        currentUser: normalizedUser,
        loading: false,
        error: null,
      };
    }
  }
  
  return {
    isAuthenticated: false,
    currentUser: null,
    loading: false,
    error: null,
  };
};

const initialState: AuthState = getInitialState();

const AuthSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginSuccess: (state, action: PayloadAction<{ accessToken: string }>) => {
      state.isAuthenticated = true;
      const decoded = decodeJWT(action.payload.accessToken) as any;
      if (decoded) {
        let perms = decoded.permission || [];
        if (typeof perms === 'string') perms = [perms];

        const normalizedUser: UserPayload = {
          nameid: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] || decoded.sub || decoded.nameid,
          email: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] || decoded.email,
          role: decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || decoded.role,
          permissions: perms,
          fullName: decoded.fullName,
          cabinetName: decoded.groupLabel,
        };
        state.currentUser = normalizedUser;
      }
      state.error = null;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.currentUser = null;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('docarn_current_user');
    },
    setAuthError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
  },
});

export const { loginSuccess, logout, setAuthError } = AuthSlice.actions;

export default AuthSlice.reducer;
